import { Container, Typography } from "@material-ui/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import generatePDF from "react-to-pdf";
import {
  getInvoiceIssuerData,
  getInvoiceIssuers,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import i18n from "../../i18n";
import CTN_logo from "../../images/ctnfi.png";

const server = EnvSettings.server;

const roundToTwo = (value) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return 0;
  }
  return Math.round((Number(value) + 1e-9) * 100) / 100;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "";
  }
  return roundToTwo(value).toFixed(2);
};

const calculateDueDate = (days) => {
  const due = new Date();
  due.setDate(due.getDate() + days);
  return due.toISOString().slice(0, 10);
};

const buildPaymentInstructions = (issuerInfo, t) => {
  const lines = [];
  if (!issuerInfo) {
    return lines;
  }
  const addLine = (label, value) => {
    if (value) {
      lines.push(label + " " + value);
    }
  };
  lines.push(t("orders.paymentInstructions"));
  addLine(t("orders.bankName") + ":", issuerInfo.bankName);
  addLine(t("orders.bankAddress") + ":", issuerInfo.bankAddress);
  addLine(t("orders.bankSwift") + ":", issuerInfo.bankSwift);
  addLine(t("orders.bankCode") + ":", issuerInfo.bankCode);
  addLine(
    t("orders.accountName") + ":",
    issuerInfo.accountName || issuerInfo.name
  );
  addLine(t("orders.iban") + ":", issuerInfo.bankIban);
  lines.push(t("orders.countryOfOrigin"));
  lines.push("FINLAND");
  lines.push(t("orders.manufacturerConsigner"));
  lines.push("Cryotech Finland Oy");
  lines.push("Jaakonkatu 2, 01620 Vantaa, Finland");
  return lines;
};

const getPrepaymentValues = (
  total,
  vat,
  depositAmount,
  depositPercentage
) => {
  const vatRate = Number(vat || 0);
  const vatMultiplier = 1 + vatRate / 100;
  const amount = Number(depositAmount);
  if (!isNaN(amount) && depositAmount !== "" && depositAmount != null) {
    return {
      exclVat: vatMultiplier ? amount / vatMultiplier : amount,
      inclVat: amount,
    };
  }
  const percentage = Number(depositPercentage);
  if (
    !isNaN(percentage) &&
    depositPercentage !== "" &&
    depositPercentage != null
  ) {
    const baseTotal = Number(total || 0);
    if (percentage >= 100) {
      return {
        exclVat: baseTotal,
        inclVat: baseTotal * vatMultiplier,
      };
    }
    const exclVat = (baseTotal * percentage) / 100;
    return {
      exclVat,
      inclVat: exclVat * vatMultiplier,
    };
  }
  return null;
};

export default function InvoicePreview2({
  show,
  submit,
  setSubmit,
  setShow,
  selectedProducts,
  currency,
  totalPrice,
  clientInfoObject,
  shippingInfo,
  issuer,
  saveContact,
  invoiceLang,
  details,
  vat,
  orderNumber,
  editedOrder,
}) {
  const componentRef = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);

  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  const downloadPdf = () =>
    generatePDF(
      () => {
        return componentRef.current;
      },
      {
        method: submit ? "build" : "open",
      }
    );

  const submitOrder = (file) => {
    const data = new FormData();
    data.append("token", getToken());
    if (!editedOrder) {
      data.append("clientInfo", JSON.stringify(clientInfoObject));
      data.append("deviceList", JSON.stringify(selectedProducts));
      if (shippingInfo) {
        data.append("shippingInfo", JSON.stringify(shippingInfo));
      }
      data.append("totalPrice", totalPrice);
      data.append("currency", currency);
      data.append("invoiceIssuer", issuer);
      data.append("saveContact", saveContact);
      data.append("details", details || "");
      data.append("vat", vat);
      data.append("orderNumber", orderNumber);
    } else {
      data.append("orderData", JSON.stringify(editedOrder));
    }
    var v = file.output("blob");
    v = new File([v], (orderNumber || crypto.randomUUID()) + ".pdf", {
      type: "application/pdf",
    });
    data.append("invoice", v);
    const requestOptions = {
      method: "POST",
      body: data,
    };
    fetch(server + (editedOrder ? "/edit_order" : "/add_order"), requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          window.location.reload();
        }
        setSubmit(false);
      });
  };

  useEffect(() => {
    if (show && componentRef.current) {
      var oldlang = i18n.language;
      if (invoiceLang && invoiceLang !== i18n.language) {
        i18n.changeLanguage(invoiceLang).then(() => {
          downloadPdf().then((pdf) => {
            if (submit) {
              submitOrder(pdf);
            }
            if (invoiceLang !== oldlang) {
              i18n.changeLanguage(oldlang);
            }
          });
          setShow(false);
        });
      } else {
        downloadPdf().then((pdf) => {
          if (submit) {
            submitOrder(pdf);
          }
        });
        setShow(false);
      }
    }
    // eslint-disable-next-line
  }, [show, componentRef]);

  const issuerInfo = issuer ? getInvoiceIssuerData(invoiceIssuers, issuer) : null;
  const orderNo = orderNumber || editedOrder?.number || "";
  const invoiceNo = orderNo;
  const today = new Date().toISOString().slice(0, 10);
  const dueDate = calculateDueDate(7);
  const lateInterest = "0,05% per day";
  const invoiceType = editedOrder?.metadata?.invoiceType || "proforma";

  const items = selectedProducts || [];
  const vatAmount = Number(totalPrice || 0) * (Number(vat || 0) / 100);
  const totalInclVat = Number(totalPrice || 0) + vatAmount;
  const depositPercentage =
    editedOrder?.metadata?.depositPercentage ?? 100;
  const prepayment = getPrepaymentValues(
    totalPrice,
    vat,
    null,
    depositPercentage
  );
  const showPrepayment =
    invoiceType === "proforma" &&
    prepayment &&
    !isNaN(prepayment.exclVat) &&
    prepayment.exclVat > 0;
  const paymentStatus = editedOrder?.paymentStatus;
  const depositPaidAmount =
    prepayment && !isNaN(prepayment.inclVat) ? prepayment.inclVat : 0;
  let paidAmount = 0;
  if (paymentStatus === "completed" || paymentStatus === "transit") {
    paidAmount = totalInclVat;
  } else if (paymentStatus === "deposit") {
    paidAmount = depositPaidAmount;
  }
  paidAmount = roundToTwo(paidAmount);
  const unpaidAmount = roundToTwo(Math.max(0, totalInclVat - paidAmount));
  const paymentInstructions =
    invoiceType === "proforma" ? buildPaymentInstructions(issuerInfo, t) : [];

  const itemRows = useMemo(() => {
    return items.map((item) => {
      const unitPrice =
        Number(item.price || 0) * (1 - Number(item.discount || 0) / 100);
      return {
        id: item.id || item.sku || item.name,
        name: item.name || "",
        count: item.count || 0,
        unit: item.unit || "",
        unitPrice,
        total: unitPrice * (item.count || 0),
        hsCodes: item.PackingDetail?.hsCodes || [],
      };
    });
  }, [items]);

  const primaryColor = "#003269";
  const lightBorder = "#c7d2e3";

  const rowStyle = {
    display: "flex",
    width: "100%",
  };

  const cellStyle = (width, align) => ({
    width,
    padding: "6px 6px",
    textAlign: align || "left",
    fontSize: 12,
    borderBottom: "1px solid " + lightBorder,
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
  });

  const headerCellStyle = (width, align) => ({
    width,
    padding: "6px 6px",
    textAlign: align || "left",
    fontSize: 12,
    color: "#ffffff",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
  });

  const summaryRowStyle = (bold, highlight) => ({
    display: "flex",
    width: "100%",
    padding: "4px 0",
    backgroundColor: highlight ? "rgba(0,50,105,0.1)" : "transparent",
    fontWeight: bold ? "bold" : "normal",
    fontSize: 12,
    color: primaryColor,
  });

  return show ? (
    <div
      ref={componentRef}
      style={{ position: "absolute", top: 2000, width: 900 }}
    >
      <Container
        style={{ fontFamily: "Helvetica, Arial, sans-serif", paddingTop: 30 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography
            style={{
              fontSize: 25,
              fontWeight: "bold",
              color: primaryColor,
              marginTop: 10,
            }}
          >
            {invoiceType === "commercial"
              ? t("orders.commercialInvoice")
              : t("orders.proformaInvoice")}
          </Typography>
          <img
            alt="CTN logo"
            src={CTN_logo}
            style={{
              height: 30,
              marginBottom: 30,
              filter:
                "invert(1) sepia(0.1) saturate(1000) brightness(0.33) hue-rotate(150deg)",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ width: "55%" }}>
            <Typography
              style={{ fontSize: 21, color: primaryColor, fontWeight: "bold" }}
            >
              {t("orders.invoiceTo")}:
            </Typography>
            <div style={{ marginTop: 8 }}>
              <Typography style={{ fontWeight: "bold", color: primaryColor }}>
                {clientInfoObject?.companyName || ""}
              </Typography>
              <Typography style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                {clientInfoObject?.companyAddress || ""}
              </Typography>
              <Typography style={{ fontSize: 12 }}>
                {clientInfoObject?.country || ""}
              </Typography>
              {clientInfoObject?.contactName ? (
                <Typography style={{ fontSize: 12 }}>
                  Contact: {clientInfoObject.contactName}
                </Typography>
              ) : null}
              {clientInfoObject?.contactEmail ? (
                <Typography style={{ fontSize: 12 }}>
                  Email: {clientInfoObject.contactEmail}
                </Typography>
              ) : null}
              {clientInfoObject?.contactPhone ? (
                <Typography style={{ fontSize: 12 }}>
                  Phone: {clientInfoObject.contactPhone}
                </Typography>
              ) : null}
              {clientInfoObject?.companyVatId ? (
                <Typography style={{ fontSize: 12 }}>
                  VAT: {clientInfoObject.companyVatId}
                </Typography>
              ) : null}
            </div>
          </div>
          <div style={{ width: "40%" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: "100%" }}>
                {[
                  { label: t("orders.invoiceNumber"), value: invoiceNo },
                  { label: t("date") + ":", value: today },
                  { label: t("orders.dueDate") + ":", value: dueDate },
                  {
                    label: t("orders.latePaymentInterest") + ":",
                    value: lateInterest,
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{ display: "flex", fontSize: 12, gap: 8 }}
                  >
                    <div
                      style={{
                        width: "30%",
                        textAlign: "left",
                        color: primaryColor,
                        fontWeight: "bold",
                      }}
                    >
                      {row.label}
                    </div>
                    <div style={{ width: "70%", textAlign: "right" }}>
                      {row.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 10, textAlign: "right" }}>
              <Typography
                style={{
                  fontWeight: "bold",
                  color: primaryColor,
                  fontSize: 12,
                }}
              >
                {issuerInfo?.name || ""}
              </Typography>
              <Typography style={{ fontSize: 12 }}>
                {issuerInfo?.street || ""}
              </Typography>
              <Typography style={{ fontSize: 12 }}>
                {issuerInfo?.location || ""}
              </Typography>
              <Typography style={{ fontSize: 12 }}>
                {issuerInfo?.vat || ""}
              </Typography>
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: "2px solid " + primaryColor,
            marginTop: 16,
          }}
        />

        {details ? (
          <div style={{ marginTop: 8 }}>
            <Typography style={{ fontWeight: "bold", fontSize: 12 }}>
              {t("details")}:
            </Typography>
            <Typography style={{ fontSize: 12 }}>{details}</Typography>
          </div>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <div
            style={{
              ...rowStyle,
              backgroundColor: primaryColor,
            }}
          >
            <div style={headerCellStyle("45%")}>{t("description")}</div>
            <div style={headerCellStyle("10%")}>{t("orders.quantity")}</div>
            <div style={headerCellStyle("10%")}>{t("orders.unit")}</div>
            <div style={headerCellStyle("15%", "right")}>
              {t("orders.unitPrice")}
            </div>
            <div style={headerCellStyle("20%", "right")}>
              {t("orders.totalExclVat")}
            </div>
          </div>
          {itemRows.map((row) => (
            <div key={row.id} style={rowStyle}>
              <div style={cellStyle("45%")}>
                <div>{row.name}</div>
                {row.hsCodes.length ? (
                  <div style={{ fontSize: 9 }}>
                    {row.hsCodes
                      .map((code) =>
                        code?.code ? t("orders.hsCode") + ": " + code.code : ""
                      )
                      .filter(Boolean)
                      .join("\n")}
                  </div>
                ) : null}
              </div>
              <div style={cellStyle("10%")}>{row.count}</div>
              <div style={cellStyle("10%")}>{row.unit}</div>
              <div style={cellStyle("15%", "right")}>
                {formatCurrency(row.unitPrice)}
              </div>
              <div style={cellStyle("20%", "right")}>
                {formatCurrency(row.total)}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div style={{ width: "35%", marginTop: 12 }}>
            <div style={summaryRowStyle(false, false)}>
              <div style={{ width: "60%", textAlign: "right" }}>
                {t("orders.subtotal")} 0%
              </div>
              <div style={{ width: "40%", textAlign: "right", color: "#000" }}>
                {formatCurrency(totalPrice)}
              </div>
            </div>
            <div style={summaryRowStyle(false, false)}>
              <div style={{ width: "60%", textAlign: "right" }}>
                {t("orders.vat")} {vat || 0}%
              </div>
              <div style={{ width: "40%", textAlign: "right", color: "#000" }}>
                {formatCurrency(vatAmount)}
              </div>
            </div>
                <div style={summaryRowStyle(true, true)}>
                  <div style={{ width: "60%", textAlign: "right" }}>
                    {t("orders.invoiceTotal")} ({currency})
                  </div>
                  <div style={{ width: "40%", textAlign: "right", color: "#000" }}>
                    {formatCurrency(totalInclVat)}
                  </div>
                </div>
                {invoiceType === "commercial" ? (
                  <>
                    <div style={summaryRowStyle(false, false)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.paid")} ({currency})
                      </div>
                      <div
                        style={{ width: "40%", textAlign: "right", color: "#000" }}
                      >
                        {formatCurrency(paidAmount)}
                      </div>
                    </div>
                    <div style={summaryRowStyle(true, true)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.unpaid")} ({currency})
                      </div>
                      <div
                        style={{ width: "40%", textAlign: "right", color: "#000" }}
                      >
                        {formatCurrency(unpaidAmount)}
                      </div>
                    </div>
                  </>
                ) : null}
                {showPrepayment ? (
                  <>
                    <div style={summaryRowStyle(false, false)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.prepaymentWithoutVat")}
                      </div>
                      <div
                        style={{ width: "40%", textAlign: "right", color: "#000" }}
                      >
                        {formatCurrency(prepayment.exclVat)}
                      </div>
                    </div>
                    <div style={summaryRowStyle(true, true)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.prepaymentTotal")} ({currency})
                      </div>
                      <div
                        style={{ width: "40%", textAlign: "right", color: "#000" }}
                      >
                        {formatCurrency(prepayment.inclVat)}
                      </div>
                    </div>
                  </>
            ) : null}
          </div>
        </div>
        {invoiceType === "proforma" && paymentInstructions.length ? (
          <div style={{ marginTop: 16, display: "flex" }}>
            <div style={{ width: "60%" }}>
              {paymentInstructions.map((line, idx) => (
                <Typography
                  key={idx}
                  style={{
                    fontSize: idx === 0 ? 11 : 10,
                    fontWeight: idx === 0 ? "bold" : "normal",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {line}
                </Typography>
              ))}
            </div>
          </div>
        ) : null}

        <div
          style={{
            borderTop: "1px solid " + lightBorder,
            marginTop: 18,
            paddingTop: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ width: "60%" }}>
              <Typography style={{ fontSize: 9 }}>
                {issuerInfo?.companyPhone
                  ? t("orders.phone") + " " + issuerInfo.companyPhone
                  : ""}
              </Typography>
              <Typography style={{ fontSize: 9 }}>
                {issuerInfo?.companyEmail
                  ? "   " + t("orders.email") + " " + issuerInfo.companyEmail
                  : ""}
              </Typography>
              <Typography style={{ fontSize: 9 }}>
                {issuerInfo?.companyWebsite
                  ? "   " +
                    t("orders.homepage") +
                    " " +
                    issuerInfo.companyWebsite
                  : ""}
              </Typography>
            </div>
            <div style={{ width: "35%", textAlign: "right" }}>
              <Typography style={{ fontSize: 9 }}>
                {(issuerInfo?.bankName ? issuerInfo.bankName : "") +
                  (issuerInfo?.bankSwift
                    ? " " + t("orders.swift") + " " + issuerInfo.bankSwift
                    : "")}
              </Typography>
              <Typography style={{ fontSize: 9 }}>
                {issuerInfo?.bankIban
                  ? t("orders.iban") + " " + issuerInfo.bankIban
                  : ""}
              </Typography>
            </div>
          </div>
        </div>
      </Container>
    </div>
  ) : null;
}
